// src/pages/TermsAndConditions.jsx
import React, { useState, useEffect } from "react"; // Add useState and useEffect
import { useNavigate } from "react-router-dom";
import { Link } from 'react-router-dom';
import './LandingPage.css'; // Import landing page styles for navbar, footer, background
import './TermsAndConditions.css'; // Import terms-specific styles

export default function TermsAndConditions() {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false); // Add mobile menu state
  const [isScrolled, setIsScrolled] = useState(false); // Add scroll state

  // Add scroll listener
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="container">
      <div className="background-overlay"></div>
      
      {/* Same navbar as landing page with floating effect */}
      <div className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
        <div className="logo-container">
          <Link to="/">
            <img src="/ob_logo_small_white.png" alt="OffsetBridge logo" className="logo-img" />
          </Link>
          <Link to="/" className="logo-text">OffsetBridge</Link>
        </div>
        
        {/* Desktop Navigation */}
        <div className="nav-buttons desktop-nav">
          <button className="howItWorks-btn" onClick={() => navigate("/how-it-works")}>
            How It Works
          </button>
          <button className="login-btn" onClick={() => navigate("/login")}>
            Log In
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="mobile-menu-btn" 
          onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`hamburger ${isMobileMenuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
      </div>

      {/* Mobile Menu Dropdown */}
      <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        <button 
          className="mobile-menu-item" 
          onClick={() => {
            navigate("/how-it-works");
            setMobileMenuOpen(false);
          }}
        >
          How It Works
        </button>
        <button 
          className="mobile-menu-item" 
          onClick={() => {
            navigate("/login");
            setMobileMenuOpen(false);
          }}
        >
          Log In
        </button>
      </div>

      {/* Terms & Conditions content */}
      <div className="terms-content">
        <div className="terms-card">
          <h1 className="terms-title">Terms & Conditions</h1>
          
          <div className="terms-section">
            <p>
              Last revised: November 1, 2025
            </p>
            <p>
              Welcome to OffsetBridge! 
              <br></br>These General Terms & Conditions (the “Terms”) govern your 
              access to and use of the OffsetBridge platform (the “Platform”). By accessing or 
              using the Platform, you agree to be bound by these Terms. If you do not agree, 
              please do not use the Platform.
            </p>
          </div>

          <div className="terms-section">
            <h2 className="terms-heading">1. Beta disclaimer</h2>
            <p>
            OffsetBridge is currently in beta and offered “as is” without any warranties. The Platform provides data for 
            informational purposes only. We do not guarantee the completeness, accuracy, or usefulness of any data, and 
            you use the Platform at your own risk. We reserve the right to modify, suspend, discontinue parts of the 
            Platform at any time without notice.
            </p>
          </div>

          <div className="terms-section">
            <h2 className="terms-heading">2. Intellectual property</h2>
            <p>
              You may use our services for lawful purposes only. You agree not to use the service:
              All content, code, trademarks, and logos displayed on the Platform, including the name “OffsetBridge,” 
              are the intellectual property of the creators of OffsetBridge, unless otherwise stated. You are granted 
              a limited, non-exclusive, non-transferable right to access and use the Platform for personal or internal 
              business purposes only. Unauthorized use, reproduction, or distribution is strictly prohibited. Any 
              attempt to copy, reverse-engineer, or decompile the Platform or its components is forbidden.
            </p>
          </div>

          <div className="terms-section">
            <h2 className="terms-heading">3. Acceptable registration and acceptable use</h2>
            <p>
              To access certain features, you may need to create an account. You agree to provide accurate, complete, and current information, and to keep your login credentials confidential.
            </p>
            <p>
              You agree not to:
            <ul className="terms-list">
              <li>use the Platform for unlawful purposes or in any way that could damage, disable, or impair it;</li>
              <li>attempt to gain unauthorized access to any systems or networks connected to the Platform;</li>
              <li>resell, redistribute, or commercialize data provided through OffsetBridge without written permission.</li>
            </ul>
            We reserve the right to suspend or terminate your access in case of breach of these Terms or misuse of the Platform.
            </p>
          </div>

          <div className="terms-section">
            <h2 className="terms-heading">4. Limitation of liability</h2>
            <p>
            We are not liable for any indirect, incidental, special, or consequential damages arising from your use of the Platform.
            To the fullest extent permitted by law, our total liability for any claim related to the Platform shall not exceed the amount 
            you have paid (if any) for using the Platform during the twelve (12) months preceding the claim.
            You agree to indemnify and hold us harmless from any claims, losses, or damages resulting from your use of the Platform or 
            any violation of these Terms.

            </p>
          </div>

          <div className="terms-section">
            <h2 className="terms-heading">5. Data protection</h2>
            <p>
            Your privacy is important to us. We collect and process personal data in accordance with our Privacy Policy, which forms 
            an integral part of these Terms. By using the Platform, you acknowledge that you have read and agreed to the Privacy Policy.
            </p>
          </div>

          <div className="terms-section">
            <h2 className="terms-heading">6. Third-party services and links</h2>
            <p>
            The Platform may include links to third-party websites or services. These are provided for convenience only, and we are not responsible for 
            their content, availability, or data practices. You access such third-party services at your own risk.
            </p>
          </div>
          <div className="terms-section">
            <h2 className="terms-heading">7. Changes</h2>
            <p>
            We may update or modify these Terms from time to time to reflect operational, legal, or regulatory changes. 
            Continued use of the Platform after such changes constitutes acceptance of the updated Terms. If material 
            changes occur, we will notify you via the Platform or by email.
            </p>
          </div>
          <div className="terms-section">
            <h2 className="terms-heading">8. Termination</h2>
            <p>
            We may suspend or terminate your access to the Platform at any time, without notice, if you breach these Terms. 
            Upon termination, all rights granted to you under these Terms shall immediately cease.
            </p>
          </div>
          <div className="terms-section">
            <h2 className="terms-heading">9. Governing law and jurisdiction</h2>
            <p>
            These Terms are governed by and construed in accordance with the laws of Switzerland. Any dispute arising from or 
            related to the use of the Platform shall be subject to the exclusive jurisdiction of the courts of Zurich, Switzerland, 
            unless otherwise required by mandatory law.
            </p>
          </div>
          <div className="terms-section">
            <h2 className="terms-heading">10. Force Majeure</h2>
            <p>
            We shall not be held liable for any delay or failure to perform resulting from causes beyond our reasonable control, 
            including but not limited to natural disasters, network failures, strikes, wars, or legal restrictions.
            </p>
          </div>
          <div className="terms-section">
            <h2 className="terms-heading">11. Contact Information</h2>
            <p>
              For questions or concerns, please contact, please contact us at{" "}
              <a href="mailto:team@offsetbridge.com" className="terms-email">team@offsetbridge.com</a>.
            </p>
          </div>
        </div>
      </div>

      {/* Same footer as landing page */}
      <div className="footer">
        © 2025 OffsetBridge
        <Link to="/privacy-policy" className="privacy-link">
          Privacy Policy
        </Link>
        <Link to="/terms-and-conditions" className="terms-link">
          Terms & Conditions
        </Link>
      </div>
    </div>
  );
}